import type { Href } from "expo-router";
import { router } from "expo-router";
import { useEffect, useState } from "react";
import { Platform, ScrollView, Text, View } from "react-native";

import { BrandBackdrop } from "../../../components/student/BrandBackdrop";
import { StudentPortalShell } from "../../../components/student/StudentPortalShell";
import {
  ActivityInboxBody,
  STUDENT_FILTERS,
} from "../../../components/notifications/ActivityInboxBody";
import { listMyNotifications } from "../../../lib/notifications";
import { useStudentPortalShellData } from "../../../lib/useStudentPortalShellData";

import { COLORS, FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;


/**
 * v2 student Activity page. Replaces the retired redirect. Renders a
 * design-aligned hero ("Everything *your team* has been up to.") on top
 * of the existing `ActivityInboxBody` body — the filter dropdown + per-
 * kind icon rows the overlay surface already uses. The hero's kicker
 * shows a live unread count + window so the page reads as a feed, not
 * a dashboard.
 */
export default function StudentActivityScreen() {
  const shell = useStudentPortalShellData();
  const [unread, setUnread] = useState<number>(0);

  // Lightweight prefetch just for the hero kicker; the inbox body does its
  // own fetch on focus, so this is purely cosmetic and is allowed to lag.
  useEffect(() => {
    let cancelled = false;
    void listMyNotifications(80).then((rows) => {
      if (cancelled) return;
      setUnread(rows.filter((r) => !r.read_at).length);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <StudentPortalShell
      topbarTitle="Activity"
      topbarFirstName={shell.topbarFirstName}
      avatarName={shell.avatarName}
      avatarUrl={shell.avatarUrl}
      identity={shell.identity}
      casesBadgeCount={shell.casesBadgeCount}
      hideSidebar
    >
      <ScrollView
        className="flex-1"
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ paddingBottom: 80 }}
      >
        <BrandBackdrop tone="sand" />
        <Hero unread={unread} onFind={() => router.push("/student/find" as Href)} />
        <View className="max-w-[1280px] px-2 pt-2 md:px-7" style={{ alignSelf: "center", width: "100%" }}>
          <ActivityInboxBody theme="light" filters={STUDENT_FILTERS} hideHero />
        </View>
      </ScrollView>
    </StudentPortalShell>
  );
}

function Hero({ unread, onFind: _onFind }: { unread: number; onFind: () => void }) {
  const isWeb = Platform.OS === "web";
  const kicker =
    unread === 0
      ? "All caught up · last 7 days"
      : unread === 1
      ? "1 unread · last 7 days"
      : `${unread} unread · last 7 days`;

  return (
    <View
      style={{
        paddingHorizontal: 24,
        paddingTop: 28,
        paddingBottom: 24,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.SAND_DARK.hex,
        position: "relative",
      }}
      className="md:px-11"
    >
      <View
        pointerEvents="none"
        style={[
          { position: "absolute", left: 24, right: 24, bottom: -1, height: 1 },
          isWeb
            ? ({
                backgroundImage:
                  "linear-gradient(90deg, transparent, rgba(181,135,58,0.45), transparent)",
              } as object)
            : { backgroundColor: STUDENT_THEME.accentBorderSoft },
        ]}
      />

      <View style={{ flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <View style={{ height: 1, width: 28, backgroundColor: COLORS.AMBER.hex }} />
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 10,
            letterSpacing: 2.2,
            textTransform: "uppercase",
            color: COLORS.AMBER.hex,
          }}
        >
          {kicker}
        </Text>
      </View>

      <Text
        style={{
          fontFamily: FONTS.display,
          fontSize: 44,
          lineHeight: 46,
          letterSpacing: -0.5,
          color: COLORS.FOREST.hex,
          marginBottom: 8,
        }}
      >
        Everything{" "}
        <Text
          style={{
            fontFamily: FONTS.display,
            fontStyle: "italic",
            color: COLORS.AMBER.hex,
          }}
        >
          your team
        </Text>{" "}
        has been up to.
      </Text>
      <Text
        style={{
          fontFamily: FONTS.body,
          fontSize: 13.5,
          lineHeight: 21,
          color: STUDENT_THEME.textOnSurfaceDim,
          maxWidth: 580,
        }}
      >
        A unified feed across all your cases. Filter by category and click any row to expand the
        details inline.
      </Text>
    </View>
  );
}
